import { MetricFormat } from '@/shared/types/campaigns';

/** Shown for a `null` value, which means not measurable rather than a measured 0. */
export const NOT_MEASURABLE = '—';

const countFormatter = new Intl.NumberFormat(undefined);

/** Cached by currency and decimal count: these run on every chart tooltip frame. */
const currencyFormatters = new Map<string, Intl.NumberFormat>();

/** An invalid ISO currency code throws RangeError; retry without the currency style. */
const safeNumberFormat = (options: Intl.NumberFormatOptions) => {
  try {
    return new Intl.NumberFormat(undefined, options);
  } catch (error) {
    if (!(error instanceof RangeError)) {
      throw error;
    }
    const { style: _style, currency: _currency, ...bare } = options;
    return new Intl.NumberFormat(undefined, bare);
  }
};

/**
 * The currency code is optional — without one we render a bare number rather than
 * guess a symbol. Decimals follow the value, since JSON drops trailing zeros.
 */
const formatCurrency = (value: number, currency?: string) => {
  const minimumFractionDigits = Number.isInteger(value) ? 0 : 2;
  const cacheKey = `${currency ?? ''}:${minimumFractionDigits}`;

  let formatter = currencyFormatters.get(cacheKey);
  if (!formatter) {
    formatter = safeNumberFormat({
      minimumFractionDigits,
      maximumFractionDigits: 2,
      ...(currency && { style: 'currency', currency }),
    });
    currencyFormatters.set(cacheKey, formatter);
  }
  return formatter.format(value);
};

/** Rates arrive as 0-1 fractions; the design renders whole percents. */
const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

const shareFormatter = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * A 0-1 fraction to two decimals. For the promised share, where the amounts are
 * small against the debt and whole percents round most campaigns to 0%.
 */
export const formatShare = (value: number): string => shareFormatter.format(value);

export const formatMetricValue = (
  value: number | null | undefined,
  format: MetricFormat,
  currency?: string
): string => {
  if (value == null) {
    return NOT_MEASURABLE;
  }
  if (format === 'currency') {
    return formatCurrency(value, currency);
  }
  if (format === 'percent') {
    return formatPercent(value);
  }
  return countFormatter.format(value);
};

const compactFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Short form for chart axis ticks, where the gutter is fixed: `R$3.4M`, `850K`.
 * A full `R$3,400,000` overflows and the chart clips it. Tooltips keep the exact value.
 *
 * Values under 1000 are unaffected, so small campaigns read the same as before.
 */
export const formatMetricTick = (
  value: number,
  format: MetricFormat,
  currency?: string
): string => {
  if (format === 'percent') {
    return formatPercent(value);
  }

  const cacheKey = `${format}:${currency ?? ''}`;
  let formatter = compactFormatters.get(cacheKey);
  if (!formatter) {
    formatter = safeNumberFormat({
      notation: 'compact',
      maximumFractionDigits: 1,
      ...(format === 'currency' && currency && { style: 'currency', currency }),
    });
    compactFormatters.set(cacheKey, formatter);
  }
  return formatter.format(value);
};
